import { SurveyTypes } from '#shared/types/Survey'

export type TestTreeGroupId = 'osobnost' | 'hodnoty' | 'kariera' | 'vyvoj' | 'vztahy'
export type TestTreeStatus = 'not_started' | 'in_progress' | 'completed'
export type TestTreeFilter = 'all' | TestTreeStatus

/** A reading order, not prerequisites: every test stays available. */
export const TEST_TREE_GROUPS = [
    {
        id: 'osobnost',
        name: 'Osobnosť',
        question: 'Kto som?',
        color: '#a9a2ff',
        cover: 'big-five',
        surveyTypes: [SurveyTypes.BIG_FIVE, SurveyTypes.SELF_KNOWLEDGE]
    },
    {
        id: 'hodnoty',
        name: 'Hodnoty a motivácia',
        question: 'Čo ma poháňa?',
        color: '#c59bff',
        cover: 'hodnoty',
        surveyTypes: [SurveyTypes.SCHWARTZ, SurveyTypes.SDT, SurveyTypes.VIA, SurveyTypes.HELP_SLOVAKIA]
    },
    {
        id: 'kariera',
        name: 'Kariéra',
        question: 'Kam smerujem?',
        color: '#efbe86',
        cover: 'karierne-zaujmy',
        surveyTypes: [SurveyTypes.HOLLAND, SurveyTypes.EXCELLENCE, SurveyTypes.ENTREPRENEURSHIP]
    },
    {
        id: 'vyvoj',
        name: 'Vývoj',
        question: 'Ako rastiem?',
        color: '#85d8cd',
        cover: 'dospely-vyvoj',
        surveyTypes: [SurveyTypes.GROWTH_MINDSET, SurveyTypes.KEGAN, SurveyTypes.KOHLBERG, SurveyTypes.KEGAN_ORDERS]
    },
    {
        id: 'vztahy',
        name: 'Vzťahy a vedenie',
        question: 'Ako sa spájam?',
        color: '#edacc8',
        cover: 'vztahove-styly',
        surveyTypes: [
            SurveyTypes.ATTACHMENT,
            SurveyTypes.LISTENING_LEVELS,
            SurveyTypes.LEADERSHIP_STYLES,
            SurveyTypes.LALOUX_PARADIGMS
        ]
    }
] satisfies Array<{
    id: TestTreeGroupId
    name: string
    question: string
    color: string
    cover: string
    surveyTypes: SurveyTypes[]
}>

export interface TestTreeEntry {
    slug: string
    title: string
    subtitle: string
    description: string
    duration: string
    questions: number
    surveyType: SurveyTypes
    status: TestTreeStatus
    progress: number
    link: string
    resultDisplay: { primaryLabel: string; secondaryLabel?: string; details?: string[] } | null
}

/** Benefits describe reflection topics; they don't promise diagnoses or validated predictions. */
export const TEST_TREE_BENEFITS: Partial<Record<SurveyTypes, [string, string, string]>> = {
    [SurveyTypes.BIG_FIVE]: [
        'Spoznaj päť stránok svojej osobnosti.',
        'Pomenuj svoje prirodzené tendencie.',
        'Premýšľaj, ako sa prejavujú v práci aj vo vzťahoch.'
    ],
    [SurveyTypes.SELF_KNOWLEDGE]: [
        'Pozri sa na seba z viacerých strán.',
        'Preskúmaj tímovosť, výkonnosť aj sebareflexiu.',
        'Prepoj svoje silné stránky s ďalšími krokmi.'
    ],
    [SurveyTypes.SCHWARTZ]: [
        'Objav, na čom ti záleží.',
        'Ujasni si svoje životné priority.',
        'Zamysli sa, ako sa premietajú do rozhodnutí.'
    ],
    [SurveyTypes.SDT]: [
        'Zisti, čo ti dáva energiu.',
        'Preskúmaj svoje dôvody pri práci, učení a pomoci.',
        'Rozlíš, čo robíš pre seba a čo pod tlakom okolia.'
    ],
    [SurveyTypes.VIA]: [
        'Pomenuj svoje charakterové silné stránky.',
        'Objav, ktoré z 24 stránok sú ti najbližšie.',
        'Hľadaj spôsoby, ako ich využívať každý deň.'
    ],
    [SurveyTypes.HELP_SLOVAKIA]: [
        'Objav svoj spôsob, ako pomáhať.',
        'Premýšľaj, na ktorých témach ti záleží.',
        'Preskúmaj možnosti svojho zapojenia do spoločnosti.'
    ],
    [SurveyTypes.HOLLAND]: [
        'Nájdi oblasti práce, ktoré ťa priťahujú.',
        'Preskúmaj šesť typov kariérnych záujmov.',
        'Získaj podnety pre ďalšie kariérne rozhodnutia.'
    ],
    [SurveyTypes.EXCELLENCE]: [
        'Preskúmaj svoj prístup ku kvalite a učeniu.',
        'Pozri sa na vytrvalosť, adaptabilitu a zlepšovanie.',
        'Vyber si oblasť, ktorú chceš ďalej rozvíjať.'
    ],
    [SurveyTypes.ENTREPRENEURSHIP]: [
        'Objav, ako premieňaš nápady na činy.',
        'Preskúmaj svoju iniciatívu a podnikavosť.',
        'Zamysli sa nad svojím prínosom pre okolie.'
    ],
    [SurveyTypes.GROWTH_MINDSET]: [
        'Pozri sa, ako pristupuješ k výzvam.',
        'Preskúmaj svoje postoje k učeniu a zmene.',
        'Všímaj si oblasti, v ktorých môžeš skúsiť nový prístup.'
    ],
    [SurveyTypes.KEGAN]: [
        'Preskúmaj svoj spôsob uvažovania.',
        'Premýšľaj o vlastnej perspektíve a pohľadoch druhých.',
        'Získaj podnety na ďalšiu sebareflexiu.'
    ],
    [SurveyTypes.KOHLBERG]: [
        'Preskúmaj, ako uvažuješ o správnom a nesprávnom.',
        'Zamysli sa nad dôvodmi svojich etických rozhodnutí.',
        'Pozri sa na dilemy z rôznych perspektív.'
    ],
    [SurveyTypes.KEGAN_ORDERS]: [
        'Pozri sa, ako dávaš zmysel svojim skúsenostiam.',
        'Preskúmaj rôzne spôsoby vnímania seba a sveta.',
        'Získaj ďalšie otázky pre svoj osobný rast.'
    ],
    [SurveyTypes.ATTACHMENT]: [
        'Pochop svoje tendencie vo vzťahoch.',
        'Zamysli sa nad blízkosťou, dôverou a samostatnosťou.',
        'Všímaj si, čo potrebuješ vo vzťahoch.'
    ],
    [SurveyTypes.LISTENING_LEVELS]: [
        'Objav, ako počúvaš druhých.',
        'Preskúmaj, kam smeruje tvoja pozornosť v rozhovore.',
        'Získaj podnet na vedomejšie počúvanie.'
    ],
    [SurveyTypes.LEADERSHIP_STYLES]: [
        'Preskúmaj svoj spôsob vedenia.',
        'Porovnaj šesť štýlov podľa Golemana.',
        'Premýšľaj, kedy pomôže ktorý prístup.'
    ],
    [SurveyTypes.LALOUX_PARADIGMS]: [
        'Preskúmaj kultúru svojho pracovného prostredia.',
        'Pozri sa na spôsob spolupráce a rozhodovania.',
        'Ujasni si, v akom prostredí sa ti dobre pracuje.'
    ]
}

export function testTreeCover(slug: string): string {
    return `/images/sebapoznanie/tree/${slug}.webp`
}

/** The same cover cut out on a transparent background, for cards on light and dark surfaces. */
export function testCoverImage(slug: string): string {
    return `/images/sebapoznanie/covers/${slug}.webp`
}

export function testTreeStatusLabel(status: TestTreeStatus): string {
    return { completed: 'Hotovo', in_progress: 'V procese', not_started: 'Na objavenie' }[status]
}

export function suggestedTreeTest(tests: TestTreeEntry[]): TestTreeEntry | undefined {
    return (
        tests.find((test) => test.status === 'in_progress') ??
        tests.find((test) => test.surveyType === SurveyTypes.BIG_FIVE && test.status === 'not_started') ??
        tests.find((test) => test.status === 'not_started') ??
        tests[0]
    )
}
